import { Progress } from '@base-ui/react';
import type { Theme } from '@emotion/react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import type { ProgressColor, ProgressDirection, ProgressSize } from './types';

const indeterminateSlide = keyframes`
  0% {
    left: -40%;
    width: 40%;
  }
  60% {
    left: 100%;
    width: 100%;
  }
  100% {
    left: 100%;
    width: 40%;
  }
`;

const shimmerSlide = keyframes`
  from {
    background-position: 0 0;
  }
  to {
    background-position: 28px 0;
  }
`;

const trackHeight: Record<ProgressSize, number> = {
  xs: 2,
  sm: 4,
  md: 8,
  lg: 12,
};

function getIndicatorColor(theme: Theme, color: ProgressColor): string {
  switch (color) {
    case 'info':
      return theme.colors.info.solid;
    case 'error':
      return theme.colors.error.solid;
    case 'warning':
      return theme.colors.warning.solid;
    case 'accent':
      return theme.colors.accent.solid;
    case 'success':
      return theme.colors.success.solid;
    default:
      return theme.colors.brand.solid;
  }
}

export const StyledContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$direction',
})<{ $direction: ProgressDirection }>(({ $direction }) => ({
  display: 'flex',
  flexDirection: $direction === 'horizontal' ? 'row' : 'column',
  alignItems: $direction === 'horizontal' ? 'center' : 'stretch',
  gap: $direction === 'horizontal' ? 12 : 8,
  width: '100%',
}));

export const StyledHorizontalRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
});

export const StyledBarWrapper = styled('div')({
  flex: 1,
  minWidth: 0,
});

export const StyledLinearRoot = styled(Progress.Root)({
  display: 'block',
  width: '100%',
});

export const StyledTrack = styled(Progress.Track, {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: ProgressSize }>(({ theme, $size }) => ({
  position: 'relative',
  width: '100%',
  height: trackHeight[$size],
  backgroundColor: theme.colors.background.overlay,
  borderRadius: theme.radius.full,
  overflow: 'hidden',
}));

export const StyledIndicator = styled(Progress.Indicator, {
  shouldForwardProp: (prop) => !['$color', '$shimmering'].includes(prop as string),
})<{ $color: ProgressColor; $shimmering: boolean }>(({ theme, $color, $shimmering }) => ({
  position: 'relative',
  height: '100%',
  backgroundColor: getIndicatorColor(theme, $color),
  borderRadius: theme.radius.full,
  transition: 'width 0.3s ease',
  overflow: 'hidden',

  ...($shimmering && {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: `repeating-linear-gradient(-45deg, ${theme.colors.white[20]} 0 8px, transparent 8px 16px)`,
      backgroundSize: '28px 28px',
      animation: `${shimmerSlide} 1s linear infinite`,
    },
  }),

  '&[data-indeterminate]': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '40%',
    animation: `${indeterminateSlide} 1.6s ease-in-out infinite`,
  },

  '&[data-complete]': {
    width: '100%',
  },
}));
